import { Button } from '@/components/form/Button';
import { useRegions } from '@/services/api/hooks/region';
import { useCSV, useResult } from '@/services/csv/useCSVService';
import { useCallback, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useControls } from '@/components/multistep/useMultistep';
import { IncomingRateCard } from '@/services/csv/helpers/parsers';
import DaaSTable, { DaasTableColumn } from '@/components/table/DaasTable';
import DaasTableRowCell from '@/components/table/DaasTableRowCell';
import { usePrintCurrencyString } from '@/utils/price';
import { SERVICE_REQUEST_TYPES } from '@/constants/serviceRequests';
import { useBulkUpsertRateCards } from '@/services/api/hooks/catalog';
import { useNavigate } from 'react-router-dom';
import { CSVResult } from '@/services/csv/helpers/process';

const getSrLabel = (sr: string) => {
  if (!sr || sr === 'None') return 'None';
  return SERVICE_REQUEST_TYPES.find(t => t.value === sr)?.label || sr;
};

export const RateCardReviewStep = () => {
  const result = useResult<CSVResult<IncomingRateCard>>();
  if (!result) throw Error('No result');

  const { skipToStep } = useControls();
  const [regions] = useRegions();
  const bulkUpsertRateCards = useBulkUpsertRateCards();
  const printCurrency = usePrintCurrencyString();
  const navigate = useNavigate();

  const [products, setProducts] = useState<IncomingRateCard[]>([]);

  useEffect(() => {
    if (result.data) {
      setProducts(result.data);
    }
  }, [result.data]);

  const headers: DaasTableColumn[] = [
    { accessor: 'Name', text: 'Name', size: '1.5fr' },
    { accessor: 'Product ID', text: 'Product ID', size: '1.25fr' },
    { accessor: 'Region', text: 'Region', size: 'max-content' },
    { accessor: 'Price', text: 'Price', size: '0.75fr' },
    { accessor: 'Applicable Request Types', text: 'Request Types', size: 'max-content' },
    { accessor: 'Base Pack For', text: 'Base Rate' },
    { accessor: 'Item Rate For', text: 'Item Rate' },
    { accessor: 'Max Chargeable Weight (kg)', text: 'Max Kg' }
  ];

  const finish = async () => {
    try {
      const items = products.map(p => {
        const regionCode = p.Region || 'UK';
        const region = regions.find(r => r.code.toLowerCase() === regionCode.toLowerCase());
        
        if (!region) {
          throw new Error(`Region ${regionCode} not found for ${p.Name}`);
        }

        return {
          name: p.Name,
          productId: p['Product ID'],
          regionId: region.id,
          catalogPrice: p.Price ?? { price: 0, effectiveDate: { value: new Date().toISOString() } },
          applicableRequestTypes: p['Applicable Request Types'],
          basePackForRequestType: p['Base Pack For'],
          itemRateForRequestType: p['Item Rate For'],
          maxChargeableWeightKg: p['Max Chargeable Weight (kg)'],
          isItemRateOverride: p['Is Item Rate Override'],
          isExclusiveOverride: p['Is Exclusive Crate'],
          addonCategory: p['Add-On Category'],
          isCoveredByWarranty: p['Covered By Warranty'],
          description: p.Description
        };
      });

      await bulkUpsertRateCards.mutateAsync({ items });
      toast.success('Rate Cards successfully uploaded');
      navigate('/admin/rate-cards');
    } catch (e: any) {
      toast.error(e.message || 'Failed to upload rate cards');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full max-h-[60vh] overflow-y-auto border border-gray-200 rounded-md">
        <DaaSTable headers={headers}>
          {products.map((card, idx) => (
            <div key={idx} className="contents text-sm">
              <DaasTableRowCell className="truncate text-left justify-start">{card.Name}</DaasTableRowCell>
              <DaasTableRowCell className="truncate">{card['Product ID'] || 'N/A'}</DaasTableRowCell>
              <DaasTableRowCell className="truncate">{card.Region || 'N/A'}</DaasTableRowCell>
              <DaasTableRowCell className="truncate">
                {card.Price?.price !== undefined ? printCurrency(card.Price.price, card.Region) : 'N/A'}
              </DaasTableRowCell>
              <DaasTableRowCell>
                {card['Applicable Request Types'] && card['Applicable Request Types'].length > 0 ? (
                  <div className="flex flex-col items-start w-full overflow-hidden">
                    {card['Applicable Request Types'].map((t, i) => (
                      <div key={i} className="truncate w-full">{getSrLabel(t)}</div>
                    ))}
                  </div>
                ) : (
                  'N/A'
                )}
              </DaasTableRowCell>
              <DaasTableRowCell className="truncate">{getSrLabel(card['Base Pack For'] || 'None')}</DaasTableRowCell>
              <DaasTableRowCell className="truncate">{getSrLabel(card['Item Rate For'] || 'None')}</DaasTableRowCell>
              <DaasTableRowCell className="truncate">{card['Max Chargeable Weight (kg)'] ? `${card['Max Chargeable Weight (kg)']} kg` : 'None'}</DaasTableRowCell>
            </div>
          ))}
        </DaaSTable>
      </div>
      <Button loading={bulkUpsertRateCards.isLoading} onClick={finish} className="w-fit self-end">
        {products.length === 0 ? 'No new products' : 'Upload Rate Cards'}
      </Button>
    </div>
  );
};
